import { useState, useCallback } from 'react';
import type { Picker } from '../types';
import {
    fetchPickersFromDb,
    createPickerInDb,
    deletePickerFromDb,
    updatePickerPhoneInDb,
    fetchPickerPhoneFromDb,
    type CreatePickerPayload,
} from '../services/pickerService';
import { getErrorMessage } from '../utils/errors';

export function usePickers() {
    const [pickers, setPickers] = useState<Picker[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchPickers = useCallback(async (): Promise<Picker[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchPickersFromDb();

            const phones = await Promise.all(
                data.map(p => p.auth_id
                    ? fetchPickerPhoneFromDb(p.auth_id).catch(() => '')
                    : Promise.resolve('')
                )
            );

            const withPhones = data.map((p, i) => ({ ...p, cellphone: phones[i] }));
            setPickers(withPhones);
            return withPhones;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch pickers'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createPicker = useCallback(async (
        picker: CreatePickerPayload
    ): Promise<Picker | null> => {
        setError(null);
        try {
            const created = await createPickerInDb(picker);
            setPickers(prev => [...prev, { ...created, cellphone: picker.cellphone }]);
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create picker'));
            return null;
        }
    }, []);

    const updatePickerPhone = useCallback(async (
        authId: string | number,
        cellphone: string
    ): Promise<boolean> => {
        setError(null);
        try {
            await updatePickerPhoneInDb(authId, cellphone);
            setPickers(prev => prev.map(p => p.auth_id === authId ? { ...p, cellphone } : p));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update phone'));
            return false;
        }
    }, []);

    const deletePicker = useCallback(async (pickerId: string): Promise<boolean> => {
        setError(null);
        try {
            await deletePickerFromDb(pickerId);
            setPickers(prev => prev.filter(p => String(p.id) !== pickerId));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete picker'));
            return false;
        }
    }, []);

    return { pickers, loading, error, fetchPickers, createPicker, updatePickerPhone, deletePicker };
}
